import { MarkerImage } from "@/components/MarkerImage";
import { BfSearchResultData, BfSearchResultType } from "@/lib/api/models";
import { DEBUG_MODE } from "@/lib/client";
import { MapPin, SearchGroup, SearchGroupItem, Users } from "@disasterscience/scaffoldy";
import { createContext, ReactNode, useCallback, useContext, useState } from "react";
import { FaEarthAsia } from "react-icons/fa6";
import { useLocation, useNavigate } from "react-router-dom";
import { zaraz } from "zaraz-ts";
import { useAppContext } from "./AppContext";

const SEARCH_TYPE_MAP: Record<BfSearchResultType, string> = {
    bfEvent: "Incidents & Warnings",
    position: "Locations",
    mapboxSearchResult: "Locations",
    bfUser: "Users",
};
const SUPPORTED_SEARCH_TYPES: BfSearchResultType[] = [
    BfSearchResultType.bfEvent,
    BfSearchResultType.position,
    BfSearchResultType.mapboxSearchResult,
    BfSearchResultType.bfUser,
];

interface ISearchContext {
    userSearchSelected: BfSearchResultData | null;
    handleSelect: (data: BfSearchResultData) => void;
    handleSearch: (query: string) => Promise<SearchGroup<BfSearchResultData>[]>;
}

interface SearchProviderProps {
    children: ReactNode;
}

const SearchContext = createContext<ISearchContext | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useSearch = () => {
    const context = useContext(SearchContext);
    if (!context) {
        throw new Error("useSearch must be used within a SearchProvider");
    }
    return context;
};

export const SearchProvider = ({ children }: SearchProviderProps) => {
    const { dataApi, bestRegionAvailable, getMapCenter } = useAppContext();
    const [selected, setSelected] = useState<BfSearchResultData | null>(null);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const handleSearch = (query: string): Promise<SearchGroup<BfSearchResultData>[]> => {
        const bestRegion = bestRegionAvailable();

        zaraz.track("search", {
            region: bestRegion,
        });

        let lon: number | undefined = undefined;
        let lat: number | undefined = undefined;
        const mapCenter = getMapCenter();

        if (mapCenter) {
            lon = mapCenter.lng;
            lat = mapCenter.lat;
        }

        return dataApi
            .search(query, bestRegion, lat, lon)
            .then((result) => {
                const apiResults = result.data.results;
                const convertedToUx: Map<
                    BfSearchResultType,
                    SearchGroupItem<BfSearchResultData>[]
                > = new Map();

                apiResults.forEach((r) => {
                    // Skip unsupported search types
                    if (!SUPPORTED_SEARCH_TYPES.includes(r.resultType)) {
                        return;
                    }

                    // Event image
                    let image = undefined;

                    switch (r.resultType) {
                        case BfSearchResultType.position:
                            image = <MapPin />;
                            break;
                        case BfSearchResultType.mapboxSearchResult:
                            image = <FaEarthAsia />;
                            break;
                        case BfSearchResultType.bfUser:
                            image = <Users />;
                            break;
                        case BfSearchResultType.bfEvent:
                            if (r.event && r.event.properties.marker) {
                                image = <MarkerImage marker={r.event.properties.marker} />;
                            }
                            break;
                        default:
                            break;
                    }

                    const searchResult: SearchGroupItem<BfSearchResultData> = {
                        title: r.text,
                        subtitle: r.subText ?? undefined,
                        id: r.id,
                        payload: r,
                        image,
                    };

                    if (!convertedToUx.has(r.resultType)) {
                        convertedToUx.set(r.resultType, []);
                    }

                    convertedToUx.get(r.resultType)?.push(searchResult);
                });

                const searchResult: SearchGroup<BfSearchResultData>[] = [];
                for (const [key, value] of convertedToUx.entries()) {
                    searchResult.push({
                        title: SEARCH_TYPE_MAP[key],
                        items: value,
                    });
                }

                return searchResult;
            })
            .catch((e) => {
                console.warn("Failed to search", e);
                return [];
            });
    };

    const handleSelect = useCallback(
        (data: BfSearchResultData) => {
            if (DEBUG_MODE) console.log("SearchContext: handleSelect", data);
            if (data.resultType === BfSearchResultType.bfUser) {
                navigate(`/ux/user/${data.id}`);
                return;
            }

            if (pathname === "/" || pathname.startsWith("/ux/customlayer/map/")) {
                setSelected(data);
            } else {
                navigate("/");
                setTimeout(() => {
                    setSelected(data);
                }, 2000);
            }
        },
        [navigate, pathname]
    );

    const value: ISearchContext = {
        userSearchSelected: selected,
        handleSelect,
        handleSearch,
    };

    return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};
